// Client mirror of the planning grants (spec 03 §7, D-351). The server decides;
// this only keeps the screens from offering a control that would be refused.
// `tools/ci/web-permission-mirror.ts` holds it to `systemRoleGrants`.

import {
  derivePermissions,
  hasLineage,
  holdsPermission,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The planning codes registered with the master-data, MPS, calendar, MRP and schedule routes (spec 03 §7). */
export const PLANNING_PERMISSIONS = [
  "planning.read",
  "planning.demand.manage",
  "planning.settings.manage",
  "planning.mps.edit",
  "planning.mps.approve",
  // P3-H-1 (D-390 ruling 1), migration 0091.
  "planning.calendar.read",
  "planning.calendar.manage",
  // P3-H-2 (D-390 ruling 1), migration 0092.
  "planning.mrp.run",
  "planning.planned_order.read",
  "planning.planned_order.manage",
  // P3-H-3 (D-390 ruling 1), migration 0094.
  "planning.planned_order.convert",
  // P3-I-2 (D-406 ruling 1), migration 0096.
  "planning.schedule.read",
  "planning.schedule.publish",
  // P3-I-3 (D-406 ruling 1), migration 0097: a move's money, a field grant.
  "planning.cost.read",
  // P3-J-1 (D-464 ruling 1), migration 0100: quote, commit and withdraw a
  // quoted promise. Promises themselves are read on `planning.read`.
  "planning.promise.commit",
  // P3-J J-4 (D-464 ruling 3, D-472 ruling 3), migration 0102: create, solve,
  // read and discard a what-if scenario, and promote one. Promote also needs
  // `planning.calendar.manage` and `planning.cost.read`, service-checked.
  "planning.scenario.manage",
  "planning.scenario.promote",
] as const;

export type PlanningPermission = (typeof PLANNING_PERMISSIONS)[number];

/**
 * Role code to planning codes. floor_worker holds none; qc and storekeeper
 * hold the schedule read alone, and maintenance the calendar and schedule
 * reads (D-390 ruling 1, D-406 ruling 1). A move's money is ga's, fa's and
 * finance's alone, so the planner applies moves without it (D-402 ruling 6).
 * The planner builds and solves what-if scenarios and does not promote them;
 * promote is ga's and fa's (D-464 ruling 3).
 */
export const PLANNING_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...PLANNING_PERMISSIONS],
  facility_admin: [...PLANNING_PERMISSIONS],
  planner: [
    "planning.read",
    "planning.demand.manage",
    "planning.mps.edit",
    "planning.calendar.read",
    "planning.mrp.run",
    "planning.planned_order.read",
    "planning.planned_order.manage",
    "planning.planned_order.convert",
    "planning.schedule.read",
    "planning.schedule.publish",
    "planning.promise.commit",
    "planning.scenario.manage",
  ],
  maintenance: ["planning.calendar.read", "planning.schedule.read"],
  sales: ["planning.read", "planning.schedule.read", "planning.promise.commit"],
  // Procurement reads and converts purchase orders only; the server narrows
  // (D-387 ruling 5, D-389 ruling 7).
  procurement: [
    "planning.read",
    "planning.planned_order.read",
    "planning.planned_order.convert",
    "planning.schedule.read",
  ],
  finance: [
    "planning.read",
    "planning.planned_order.read",
    "planning.schedule.read",
    "planning.cost.read",
  ],
  qc: ["planning.schedule.read"],
  storekeeper: ["planning.schedule.read"],
};

/** The effective planning set: the feed's permission list cut to this slice,
 * or the mirror without one (D-586). */
export function derivePlanningPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, PLANNING_ROLE_GRANTS);
}

export interface PlanningPermissions extends AreaPermissions, RoleSignal {
  /**
   * Stands on the group_admin lineage, the one that may move the two MPS
   * approval bars (spec 03 §2.22, D-351 ruling 8). The server keys it on the
   * lineage root (`planning-door.ts`), so a tenant's clone of group_admin
   * reads true here as well (D-586).
   */
  readonly isGroupAdmin: boolean;
  /**
   * Holds a role with `identity.view_members`, the member directory the board
   * resolves names through. It is not a planning code, so it stays out of
   * `PLANNING_ROLE_GRANTS`.
   */
  readonly canReadMembers: boolean;
  /**
   * Holds a role the server serves every planned-order kind (its
   * FULL_LINEAGES); a procurement reader alone is served purchase orders only
   * (spec 03 §7 narrowing (c), D-387 ruling 5).
   */
  readonly readsEveryPlannedOrderKind: boolean;
  /**
   * Holds a role with `sales.order.re_promise` (group_admin, facility_admin,
   * planner; spec 09 §7, D-464 ruling 2), which withdraws a committed or
   * at-risk promise (row 76). It is a sales code, so it stays out of
   * `PLANNING_ROLE_GRANTS`; the sales mirror holds it as a grant.
   */
  readonly canRePromise: boolean;
}

/** Seed roles holding `identity.view_members`: the answer for a feed row
 * without a permission list (D-586). */
const MEMBER_DIRECTORY_ROLES: ReadonlySet<string> = new Set(["group_admin", "facility_admin"]);

/** Seed roles holding `sales.order.re_promise` (spec 09 §7's Re-promise
 * row): the answer for a feed row without a permission list (D-586). */
export const RE_PROMISE_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "planner",
]);

/** Roles the planned-order read serves every kind (the server's FULL_LINEAGES). */
const EVERY_PLANNED_KIND_ROLES: ReadonlySet<string> = new Set([
  "group_admin",
  "facility_admin",
  "planner",
  "finance",
]);

export function usePlanningPermissions(): PlanningPermissions {
  const feed = useRoleCodesFeed();
  const codes = derivePlanningPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isGroupAdmin: hasLineage(feed, ["group_admin"]),
    canReadMembers: holdsPermission(feed, "identity.view_members", MEMBER_DIRECTORY_ROLES),
    // The server's FULL_LINEAGES (`mrp-json.ts`) read the lineage root.
    readsEveryPlannedOrderKind: hasLineage(feed, EVERY_PLANNED_KIND_ROLES),
    canRePromise: holdsPermission(feed, "sales.order.re_promise", RE_PROMISE_ROLES),
  };
}

export interface PlanningAreaEntry {
  readonly to:
    | "/planning"
    | "/planning/demand"
    | "/planning/forecasts"
    | "/planning/mps"
    | "/planning/orders"
    | "/planning/schedule"
    | "/planning/capacity"
    | "/planning/settings/parameters"
    | "/planning/settings/calendars"
    | "/planning/settings/setup-matrix"
    | "/planning/scenarios"
    | "/planning/promises"
    | "/planning/settings";
  readonly labelKey: string;
  readonly testId: string;
  /** The read code that opens this screen (spec 03 §4, D-390 rulings 1 and 3). */
  readonly code: PlanningPermission;
  /** The section of the area's seven that holds this screen (D-450, D-474). */
  readonly group: PlanningGroupKey;
  /** The entry's name inside its section, where it differs from `labelKey`. */
  readonly sectionLabelKey?: string;
}

/**
 * The area's seven sections (D-450, D-474 ruling 1). Eleven screens as eleven
 * tabs was more navigation than a planner reads, so the screens that answer one
 * question sit under one tab: the demand pool and the forecasts, the schedule
 * board and the capacity view, and the four settings screens. Promises (P3-J)
 * is the seventh, before Settings.
 */
export type PlanningGroupKey =
  "workbench" | "demand" | "mps" | "orders" | "schedule" | "promises" | "settings";

const GROUP_LABEL: Record<PlanningGroupKey, string> = {
  workbench: "planning.nav.workbench",
  demand: "planning.nav.demand",
  mps: "planning.nav.mps",
  orders: "planning.nav.orders",
  schedule: "planning.nav.schedule",
  promises: "planning.nav.promises",
  settings: "planning.nav.settings",
};

export interface PlanningAreaGroup {
  readonly key: PlanningGroupKey;
  readonly labelKey: string;
  /** The section's screens this reader may open, in order; never empty. */
  readonly entries: readonly PlanningAreaEntry[];
}

/**
 * Every screen of the area in sub-navigation order, each with the read code it
 * opens on. The five P3-G screens read on `planning.read`, the calendars and
 * the setup matrix on `planning.calendar.read`, the planned orders on
 * `planning.planned_order.read`, and the schedule board and the capacity view
 * on `planning.schedule.read` (D-406 ruling 3). qc and storekeeper, who hold
 * the schedule read alone, get the board and the capacity view and land on the
 * board; maintenance, who holds the calendar and schedule reads, gets those
 * four entries and lands on the board too, the first entry it holds; and
 * sales gets no planned-orders entry.
 */
const PLANNING_ENTRIES: readonly PlanningAreaEntry[] = [
  {
    to: "/planning",
    labelKey: "planning.nav.workbench",
    testId: "planning-nav-workbench",
    code: "planning.read",
    group: "workbench",
  },
  {
    to: "/planning/demand",
    labelKey: "planning.nav.demand",
    testId: "planning-nav-demand",
    code: "planning.read",
    group: "demand",
    sectionLabelKey: "planning.nav.pool",
  },
  {
    to: "/planning/forecasts",
    labelKey: "planning.nav.forecasts",
    testId: "planning-nav-forecasts",
    code: "planning.read",
    group: "demand",
  },
  {
    to: "/planning/mps",
    labelKey: "planning.nav.mps",
    testId: "planning-nav-mps",
    code: "planning.read",
    group: "mps",
  },
  {
    to: "/planning/orders",
    labelKey: "planning.nav.orders",
    testId: "planning-nav-orders",
    code: "planning.planned_order.read",
    group: "orders",
  },
  {
    to: "/planning/schedule",
    labelKey: "planning.nav.schedule",
    testId: "planning-nav-schedule",
    code: "planning.schedule.read",
    group: "schedule",
    sectionLabelKey: "planning.nav.board",
  },
  {
    to: "/planning/capacity",
    labelKey: "planning.nav.capacity",
    testId: "planning-nav-capacity",
    code: "planning.schedule.read",
    group: "schedule",
  },
  // P3-J J-4 (§4.10, D-474 ruling 1): What-if joins the Schedule section,
  // read and written on `planning.scenario.manage`; `/planning/scenarios/new`
  // and `/planning/scenarios/<id>` open on the same code.
  {
    to: "/planning/scenarios",
    labelKey: "planning.nav.whatIf",
    testId: "planning-nav-scenarios",
    code: "planning.scenario.manage",
    group: "schedule",
  },
  // P3-J-1 (§4.9, D-474 ruling 1): the promises, read on `planning.read`.
  {
    to: "/planning/promises",
    labelKey: "planning.nav.promises",
    testId: "planning-nav-promises",
    code: "planning.read",
    group: "promises",
  },
  {
    to: "/planning/settings/parameters",
    labelKey: "planning.nav.parameters",
    testId: "planning-nav-parameters",
    code: "planning.read",
    group: "settings",
  },
  {
    to: "/planning/settings/calendars",
    labelKey: "planning.nav.calendars",
    testId: "planning-nav-calendars",
    code: "planning.calendar.read",
    group: "settings",
  },
  {
    to: "/planning/settings/setup-matrix",
    labelKey: "planning.nav.setupMatrix",
    testId: "planning-nav-setup-matrix",
    code: "planning.calendar.read",
    group: "settings",
  },
  {
    to: "/planning/settings",
    labelKey: "planning.nav.settings",
    testId: "planning-nav-settings",
    code: "planning.read",
    group: "settings",
    sectionLabelKey: "planning.nav.general",
  },
];

/** The area's screens this reader may open, in sub-navigation order. */
export function planningAreaEntries(perms: Pick<AreaPermissions, "has">): PlanningAreaEntry[] {
  return PLANNING_ENTRIES.filter((entry) => perms.has(entry.code));
}

/** The area's sections this reader may open, each with its screens, in order. */
export function planningAreaGroups(perms: Pick<AreaPermissions, "has">): PlanningAreaGroup[] {
  const groups: PlanningAreaGroup[] = [];
  for (const entry of planningAreaEntries(perms)) {
    const last = groups[groups.length - 1];
    if (last?.key === entry.group) {
      groups[groups.length - 1] = { ...last, entries: [...last.entries, entry] };
    } else {
      groups.push({ key: entry.group, labelKey: GROUP_LABEL[entry.group], entries: [entry] });
    }
  }
  return groups;
}

/** The listed entry a planning path opens, or undefined for an unlisted path. */
export function planningEntryFor(pathname: string): PlanningAreaEntry | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/u, "") : pathname;
  let best: PlanningAreaEntry | undefined;
  for (const entry of PLANNING_ENTRIES) {
    const hit = path === entry.to || (entry.to !== "/planning" && path.startsWith(`${entry.to}/`));
    if (hit && (best === undefined || entry.to.length > best.to.length)) best = entry;
  }
  return best;
}

/** Where the app nav and `/planning` send this reader, or undefined when nowhere. */
export function planningLandingPath(perms: Pick<AreaPermissions, "has">): string | undefined {
  return planningAreaEntries(perms)[0]?.to;
}

/**
 * Paths with no entry of their own in the sub-navigation, each with the read
 * code it opens on: a run's detail is reached from the workbench's run history
 * and reads on the planned-order code (spec 03 §4.5, D-390 ruling 3).
 */
const PLANNING_UNLISTED: readonly { readonly to: string; readonly code: PlanningPermission }[] = [
  { to: "/planning/mrp", code: "planning.planned_order.read" },
];

/**
 * The read code a planning path opens on: the entry whose path is the longest
 * one the path equals or sits below, so `/planning/forecasts/<id>` reads on the
 * forecasts' code, `/planning/settings/calendars` on the calendars' code and
 * `/planning/mrp/runs/<id>` on the planned-order code.
 */
export function planningCodeFor(pathname: string): PlanningPermission {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/u, "") : pathname;
  let best: { to: string; code: PlanningPermission } | undefined;
  for (const entry of [...PLANNING_ENTRIES, ...PLANNING_UNLISTED]) {
    const hit = path === entry.to || path.startsWith(`${entry.to}/`);
    if (hit && (best === undefined || entry.to.length > best.to.length)) best = entry;
  }
  return best?.code ?? "planning.read";
}
